/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-12-20 11:39:10
 *
 *******************************************************/
// @flow
import React, { Component } from 'react';
import { Page, PageActions, Layout, Card, Stack, Button } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { reduxForm, reset } from 'redux-form';

import ContentSettings from './content-settings';
import Preview from './preview';
import MainMenu from 'src/components/containers/main-menu';
import DidYouKnow from 'src/components/views/quick-view-cart-settings/did-you-know';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import SendRequestFeatureModal from 'src/components/containers/common/send-request-feature-modal';
import RequestResultModal from 'src/components/containers/common/request-result-modal';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';

import { URL } from 'src/constants/links';
import { confirmLeavePage } from 'src/helpers';
import {
  buildInitialQuickViewCartSettingsValues,
  buildQuickViewCartSettingsValues
} from 'src/helpers/quick-view-cart-settings-helper';
import * as FilterSettingsActions from 'actions/filter-settings-actions';
import TitleHelp from 'src/components/views/common/title-help';

import type { ComposedProps, State } from './index.type';

class QuickViewCartSettings extends Component<ComposedProps, State> {
  state = {
    youtubeModal: false,
    // Modal send request result
    activeRequestModal: false,
    activeModalResult: false
  };

  componentDidMount() {
    const { getFilterSettings } = this.props;

    getFilterSettings();

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.loadingFilterSettings && !this.props.loadingFilterSettings) {
      startSABLoading();
    }

    if (!nextProps.loadingFilterSettings && this.props.loadingFilterSettings) {
      stopSABLoading();
    }
  }

  render() {
    const {
      dirty,
      submitting,
      handleSubmit,
      pristine,
      savingFilterSettings,
      initialValues,
      history,
      i18n
    } = this.props;
    const { youtubeModal, activeRequestModal, activeModalResult } = this.state;

    return (
      <div className="BC-Page BC-Page__QuickView-Cart-Settings">
        <MainMenu menuId="menu-theme-quick-view-cart-settings" />
        <Page
          title={i18n.translate('AdminHTML.QuickViewCartSettings.QuickViewCartSettings.pageTitle')}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <Stack>
                {!pristine && (
                  <Button disabled={submitting} onClick={this.discardChange}>
                    {i18n.translate('AdminHTML.Common.Action.discard')}
                  </Button>
                )}
                <Button
                  primary
                  onClick={handleSubmit(this.handleSave.bind(this))}
                  disabled={pristine || submitting}
                  loading={savingFilterSettings && !pristine}>
                  {i18n.translate('AdminHTML.Common.Action.saveSettings')}
                </Button>
              </Stack>
            </div>
          }>
          <TitleHelp
            i18n={i18n}
            onPlay={this.handleOpenYoutubeModal}
            hrefUserManual={URL['Link_V2_Theme_QuickViewAndCart_ManualGuide']}
          />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                <DidYouKnow sendRequest={() => this.toggleModal('activeRequestModal')} />
                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>
            <Layout.Section>
              <Card>
                <div className="content">
                  <div className="content-settings">
                    <ContentSettings initialValues={initialValues} />
                  </div>
                  <div className="preview">
                    <Preview />
                  </div>
                </div>
              </Card>
            </Layout.Section>
          </Layout>
          <PageActions
            primaryAction={{
              content: i18n.translate('AdminHTML.Common.Action.saveSettings'),
              onAction: handleSubmit(this.handleSave.bind(this)),
              disabled: pristine || submitting,
              loading: savingFilterSettings && !pristine
            }}
            secondaryActions={
              !pristine
                ? [
                    {
                      content: i18n.translate('AdminHTML.Common.Action.discard'),
                      disabled: submitting,
                      onAction: this.discardChange
                    }
                  ]
                : []
            }
          />
        </Page>
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Theme_QuickViewAndCart_Video']}
          title={i18n.translate(
            'AdminHTML.QuickViewCartSettings.QuickViewCartSettings.youtubeModal_Title'
          )}
          onClose={this.handleCloseYoutubeModal}
        />

        <SendRequestFeatureModal
          active={activeRequestModal}
          handleClose={() => this.toggleModal('activeRequestModal')}
          handleOpenResultModal={() => this.toggleModal('activeModalResult')}
          featureKey="quickViewCartSettings"
        />
        <RequestResultModal
          active={activeModalResult}
          handleClose={() => this.toggleModal('activeModalResult')}
          featureName={i18n.translate(
            'AdminHTML.Common.RequestResultModal.content_t1_quickViewCartSettings'
          )}
        />

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={dirty}
        />
      </div>
    );
  }

  // Handle click save button
  handleSave = data => {
    const { updateFilterSettings, filterSettings } = this.props;
    const saveData = buildQuickViewCartSettingsValues(data, filterSettings);
    updateFilterSettings(saveData);
  };

  discardChange = () => {
    const { resetForm } = this.props;

    resetForm();
  };

  handleOpenYoutubeModal = () => this.setState({ youtubeModal: true });
  handleCloseYoutubeModal = () => this.setState({ youtubeModal: false });

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const QuickViewCartSettingsForm = reduxForm({
  form: 'QuickViewCartSettingsForm', // a unique identifier for this form
  enableReinitialize: true
})(QuickViewCartSettings);

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterSettings: () =>
    dispatch(FilterSettingsActions.getFilterSettings(undefined, ownProps.i18n)),
  updateFilterSettings: (filterSettings: Object) =>
    dispatch(
      FilterSettingsActions.updateFilterSettings(
        filterSettings,
        'defaultLayoutSettings',
        undefined,
        ownProps.i18n
      )
    ),
  resetForm: () => dispatch(reset('QuickViewCartSettingsForm'))
});

const mapStateToProps = (state, props) => {
  const { filterSettings } = state;
  const { i18n } = props;
  return {
    filterSettings: filterSettings.filterSettings,
    loadingFilterSettings: filterSettings.loadingFilterSettings,
    savingFilterSettings: filterSettings.savingFilterSettings,
    initialValues: buildInitialQuickViewCartSettingsValues(filterSettings.filterSettings, i18n)
  };
};
export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(QuickViewCartSettingsForm));
